import { type FC, useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import { addMonths, format as formatDate, subMonths } from "date-fns";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../theme";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { CalendarMonth } from "./CalendarMonth";
import type { WeekStartsOn } from "./date-utils";

interface CalendarProps {
  readonly weekStartsOn: WeekStartsOn;
  readonly today: Date;
  readonly defaultMonth: Date;
  readonly selectedStart: Date | null;
  readonly selectedEnd: Date | null;
  readonly hoveredDate: Date | null;
  readonly rangeMode: boolean;
  readonly minDate?: Date | undefined;
  readonly maxDate?: Date | undefined;
  readonly onSelectDate: (date: Date) => void;
  readonly onHoverDate?: ((date: Date | null) => void) | undefined;
  /** Override the default 40px cell size for narrow viewports. */
  readonly cellSize?: number | undefined;
}

const Calendar: FC<CalendarProps> = ({
  weekStartsOn,
  today,
  defaultMonth,
  selectedStart,
  selectedEnd,
  hoveredDate,
  rangeMode,
  minDate,
  maxDate,
  onSelectDate,
  onHoverDate,
  cellSize,
}) => {
  const { theme } = useUnistyles();
  const [visibleMonth, setVisibleMonth] = useState(defaultMonth);

  // Follow `defaultMonth` whenever it changes (e.g. the value was cleared or
  // updated from outside while the popover stayed open). `defaultMonth` is
  // derived from the selection/value/today, so it is stable during manual
  // prev/next navigation and only changes on an external reset — keeping user
  // navigation intact while resyncing the grid when the anchor date moves.
  const defaultMonthKey = defaultMonth.getTime();
  useEffect(() => {
    setVisibleMonth(defaultMonth);
  }, [defaultMonthKey]);

  return (
    <View style={styles.calendar}>
      <View style={styles.header}>
        <Pressable
          onPress={() => setVisibleMonth((m) => subMonths(m, 1))}
          hitSlop={8}
          style={styles.navButton}
          accessibilityRole="button"
          accessibilityLabel="Previous month"
        >
          <Icon name="chevronLeft" size={20} color={theme.colors.text} />
        </Pressable>

        <Text textStyle="sm/semibold" color="text">
          {formatDate(visibleMonth, "MMMM yyyy")}
        </Text>

        <Pressable
          onPress={() => setVisibleMonth((m) => addMonths(m, 1))}
          hitSlop={8}
          style={styles.navButton}
          accessibilityRole="button"
          accessibilityLabel="Next month"
        >
          <Icon name="chevronRight" size={20} color={theme.colors.text} />
        </Pressable>
      </View>

      <CalendarMonth
        month={visibleMonth}
        weekStartsOn={weekStartsOn}
        today={today}
        selectedStart={selectedStart}
        selectedEnd={selectedEnd}
        hoveredDate={hoveredDate}
        rangeMode={rangeMode}
        minDate={minDate}
        maxDate={maxDate}
        onSelectDate={onSelectDate}
        onHoverDate={onHoverDate}
        cellSize={cellSize}
      />
    </View>
  );
};

Calendar.displayName = "Calendar";

export { Calendar };

const styles = StyleSheet.create((theme) => ({
  calendar: {
    gap: theme.spacing.sm,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
  },
  navButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
  },
}));
